import { useEffect } from 'react';
import { renewSession } from '@/shared/api/http-client';
import { useSession } from '@/features/auth/model/use-session';

/** How long before expiry to renew. A minute is enough for a slow link and short enough to be safe. */
const EARLY_MS = 60_000;

/** Never sooner than this, so a clock skew cannot turn renewal into a request loop. */
const FLOOR_MS = 5_000;

/**
 * Keeps the session alive while the panel is open (§12.1).
 *
 * ⚠️ **A control room is mostly watched, not clicked** — and a panel nobody is touching makes no
 * requests, so nothing ever provokes the 401 the interceptor refreshes on. The access token simply
 * expires. Everything still *looks* right: the last-loaded figures are on screen, no error appears,
 * and the only thing that has actually happened is that the live feed died.
 *
 * ⚠️ That is not theoretical. Measured against the live broker: an idle panel reconnected every few
 * seconds with an expired token, EMQX logged `bad_username_or_password` each time, and the screen
 * showed nothing at all.
 *
 * ⚠️ Failure is left alone here. `renewSession` clears the session when the refresh token is gone,
 * which the shell already reacts to by showing sign-in — so there is nothing for this to handle,
 * and retrying a refusal in a loop is how a revoked session becomes a hammering client.
 */
export function useSessionRenewal(): void {
  const { session } = useSession();
  const expiresAt = session?.accessTokenExpiresAt;

  useEffect(() => {
    if (!expiresAt) {
      return;
    }

    const expiry = Date.parse(expiresAt);
    if (Number.isNaN(expiry)) {
      return;
    }

    const delay = Math.max(expiry - Date.now() - EARLY_MS, FLOOR_MS);
    const timer = window.setTimeout(() => void renewSession(), delay);

    // Re-scheduled from the new expiry every time the session changes — including the renewal this
    // effect itself performed, which is what makes it repeat without a second timer.
    return () => window.clearTimeout(timer);
  }, [expiresAt]);
}
